import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedPage from '../components/shared/AnimatedPage';
import { validateConsentToken, recordConsent } from '../lib/functions';

type Step = 'loading' | 'invalid' | 'consent' | 'success' | 'declined';

function ConsentForm({
  childName,
  profileId,
  onSuccess,
  onDecline,
}: {
  childName: string;
  profileId: string;
  onSuccess: () => void;
  onDecline: () => void;
}) {
  const [generalConsent, setGeneralConsent] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [schoolConsent, setSchoolConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [declining, setDeclining] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!generalConsent) {
      setError('Please tick the required consent box to continue.');
      return;
    }

    setLoading(true);
    try {
      await recordConsent({
        profileId,
        generalConsent,
        aiConsent,
        schoolConsent,
      });
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDecline() {
    setDeclining(true);
    try {
      await recordConsent({
        profileId,
        generalConsent: false,
        aiConsent: false,
        schoolConsent: false,
      });
      onDecline();
    } catch {
      // Decline is best-effort; navigate away regardless
      onDecline();
    } finally {
      setDeclining(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
          Action required
        </div>
        <h2 className="mt-2 text-xl font-bold text-gray-900">
          Approve {childName}&apos;s account
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {childName} is ready to start building their first business on OMore.
          Review the permissions below and approve to unlock their account.
        </p>
      </div>

      <div className="space-y-3">
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-4 transition-colors hover:border-brand-300 hover:bg-brand-50">
          <input
            type="checkbox"
            checked={generalConsent}
            onChange={(e) => { setGeneralConsent(e.target.checked); setError(''); }}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <div>
            <span className="text-sm font-semibold text-gray-900">
              I approve this account
            </span>
            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
              Required
            </span>
            <p className="mt-1 text-xs text-gray-500">
              I confirm I am {childName}&apos;s parent or guardian and consent to them
              creating an account on OMore. Their data is stored securely and used
              only for the educational experience.
            </p>
          </div>
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-4 transition-colors hover:border-brand-300 hover:bg-brand-50">
          <input
            type="checkbox"
            checked={aiConsent}
            onChange={(e) => setAiConsent(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <div>
            <span className="text-sm font-semibold text-gray-900">
              AI Sidekick (KOKO)
            </span>
            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
              Optional
            </span>
            <p className="mt-1 text-xs text-gray-500">
              I consent to {childName} using KOKO, the AI learning companion.
              All conversations are moderated and age-appropriate.
            </p>
          </div>
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-4 transition-colors hover:border-brand-300 hover:bg-brand-50">
          <input
            type="checkbox"
            checked={schoolConsent}
            onChange={(e) => setSchoolConsent(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <div>
            <span className="text-sm font-semibold text-gray-900">
              School features
            </span>
            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
              Optional
            </span>
            <p className="mt-1 text-xs text-gray-500">
              I consent to {childName}&apos;s school name being used to enable
              school-based leaderboards and discovery.
            </p>
          </div>
        </label>
      </div>

      {error && (
        <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || declining || !generalConsent}
        className="duo-btn duo-btn-green w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Approving…' : `Approve ${childName}'s account`}
      </button>

      <button
        type="button"
        onClick={handleDecline}
        disabled={loading || declining}
        className="w-full text-sm text-gray-400 underline-offset-2 hover:text-red-500 hover:underline disabled:opacity-50"
      >
        {declining ? 'Declining…' : 'Decline this request'}
      </button>

      <p className="text-center text-xs text-gray-400">
        By approving, you agree to the OMore{' '}
        <a href="https://biz.omore.app/terms" className="text-brand-600 underline">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://biz.omore.app/privacy" className="text-brand-600 underline">
          Privacy Policy
        </a>
        .
      </p>
    </form>
  );
}

export default function ParentConsentPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('loading');
  const [childName, setChildName] = useState('');
  const [profileId, setProfileId] = useState('');

  useEffect(() => {
    if (!token) {
      setStep('invalid');
      return;
    }
    validateConsentToken({ token })
      .then((result) => {
        if (result.data.valid) {
          setChildName(result.data.childDisplayName);
          setProfileId(result.data.profileId);
          setStep('consent');
        } else {
          setStep('invalid');
        }
      })
      .catch(() => setStep('invalid'));
  }, [token]);

  return (
    <AnimatedPage>
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
        <motion.div
          layout
          className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-lg"
        >
          {/* OMore wordmark */}
          <div className="mb-6 flex items-center gap-2">
            <span className="text-lg font-extrabold tracking-tight text-gray-900">OMore</span>
            <span className="text-xs text-gray-400">· Parent approval</span>
          </div>

          {step === 'loading' && (
            <div className="flex flex-col items-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
              <p className="mt-4 text-sm text-gray-500">Checking your link…</p>
            </div>
          )}

          {step === 'invalid' && (
            <div className="py-12 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-3xl">
                ⚠️
              </div>
              <h1 className="mt-4 text-xl font-bold text-gray-900">
                Link expired or invalid
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                This approval link is no longer valid. Ask your child to request a
                new one from the app.
              </p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="mt-6 inline-flex min-h-touch items-center rounded-xl border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Go to Homepage
              </button>
            </div>
          )}

          {step === 'consent' && (
            <ConsentForm
              childName={childName}
              profileId={profileId}
              onSuccess={() => setStep('success')}
              onDecline={() => setStep('declined')}
            />
          )}

          {step === 'declined' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="py-8 text-center"
            >
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-gray-100 text-5xl">
                😔
              </div>
              <h1 className="mt-6 text-2xl font-extrabold text-gray-900">
                Request declined
              </h1>
              <p className="mt-3 text-sm text-gray-500">
                You&apos;ve declined {childName}&apos;s account request. They can
                try again with a different email address if they&apos;d like.
              </p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="mt-8 inline-flex min-h-touch items-center rounded-xl border border-gray-300 px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </motion.div>
          )}

          {step === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="py-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.1, stiffness: 200 }}
                className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-green-100 text-5xl"
              >
                🎉
              </motion.div>
              <h1 className="mt-6 text-2xl font-extrabold text-gray-900">
                {childName} is unlocked!
              </h1>
              <p className="mt-3 text-gray-500">
                You&apos;ve approved their account. They can now sign in and start
                building their first business on OMore.
              </p>
              <div className="mt-6 rounded-xl bg-brand-50 px-6 py-4 text-left text-sm text-brand-800">
                <p className="font-semibold">What happens next</p>
                <ul className="mt-2 space-y-1 text-brand-700">
                  <li>· {childName} can now sign in and start their adventure</li>
                  <li>· OMore is completely free — no charges or subscriptions</li>
                  <li>· You can manage their account from the Parent Portal</li>
                </ul>
              </div>
              <a
                href="https://parent.omore.app"
                className="mt-8 inline-flex min-h-touch items-center rounded-xl bg-brand-600 px-8 py-3 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Go to Parent Portal
              </a>
            </motion.div>
          )}
        </motion.div>
      </div>
    </AnimatedPage>
  );
}
